import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, ChevronRight, TrendingUp, Search, Filter } from 'lucide-react';
import { blogAPI } from '../lib/api';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';

const CATEGORY_COLORS = {
  guide: 'bg-blue-100 text-blue-700',
  comparatif: 'bg-purple-100 text-purple-700',
  test: 'bg-amber-100 text-amber-700',
  actu: 'bg-green-100 text-green-700',
};


// Default images for articles by category
const CATEGORY_IMAGES = {
  guide: 'https://images.unsplash.com/photo-1658491830143-72808ca237e3?w=800&q=80',
  comparatif: 'https://images.unsplash.com/photo-1738330194751-e096b51af7bf?w=800&q=80',
  test: 'https://images.unsplash.com/photo-1657704022321-105c8d9723ed?w=800&q=80',
  actu: 'https://images.unsplash.com/photo-1657704358775-ed705c7388d2?w=800&q=80',
};

const getArticleImage = (article) => {
  if (article.image && article.image.startsWith('http')) return article.image;
  return CATEGORY_IMAGES[article.category] || CATEGORY_IMAGES.guide;
};

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'fr').slice(0, 2);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [articlesRes, categoriesRes] = await Promise.all([
          blogAPI.getArticles(selectedCategory, lang),
          blogAPI.getCategories()
        ]);
        setArticles(articlesRes.data);
        setCategories(categoriesRes.data);
      } catch (err) {
        console.error('Error fetching blog data:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, [selectedCategory, lang]);

  const featuredArticle = articles[0];
  const otherArticles = articles.slice(1);

  return (
    <div className="min-h-screen bg-[#F0F7FA]">
      <SEO 
        title={t('blog.seo_title')}
        description={t('blog.seo_desc')}
        path="/blog"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#033871] to-[#0C63BF] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-4">
              <BookOpen className="w-4 h-4 text-[#32CD32]" />
              <span className="text-white/90 text-sm font-medium">{t('blog.badge')}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              {t('blog.hero_title')}
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
              {t('blog.hero_sub')}
            </p>
          </div>
        </div>
      </section>

      {/* Categories filter */}
      <div className="bg-white border-b border-[#E8F4FD] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            <Button
              variant={selectedCategory === null ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSelectedCategory(null)}
              className={selectedCategory === null ? 'bg-[#0C63BF] text-white' : 'text-[#5A8BA8]'}
              data-testid="filter-all"
            >
              {t('blog.all')}
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.slug}
                variant={selectedCategory === cat.slug ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setSelectedCategory(cat.slug)}
                className={selectedCategory === cat.slug ? 'bg-[#0C63BF] text-white' : 'text-[#5A8BA8]'}
                data-testid={`filter-${cat.slug}`}
              >
                {t(`blog.cat_${cat.slug}`, cat.label)} ({cat.count})
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="h-48 bg-[#E8F4FD]" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-[#E8F4FD] rounded w-1/4" />
                  <div className="h-6 bg-[#E8F4FD] rounded w-3/4" />
                  <div className="h-4 bg-[#E8F4FD] rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-[#B8D4E3] mx-auto mb-4" />
            <p className="text-[#5A8BA8] text-lg">{t('blog.empty')}</p>
          </div>
        ) : (
          <>
            {/* Featured article */}
            {featuredArticle && !selectedCategory && (
              <Link to={`/blog/${featuredArticle.slug}`} className="block mb-10" data-testid="featured-article">
                <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group">
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="h-64 md:h-80 relative overflow-hidden">
                      <img 
                        src={getArticleImage(featuredArticle)} 
                        alt={featuredArticle.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    </div>
                    <div className="p-6 sm:p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${CATEGORY_COLORS[featuredArticle.category]}`}>
                          {t(`blog.cat_${featuredArticle.category}`)}
                        </span>
                        <span className="text-[#B8D4E3] text-xs flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {featuredArticle.read_time} min
                        </span>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-[#033871] mb-3 group-hover:text-[#0C63BF] transition-colors">
                        {featuredArticle.title}
                      </h2>
                      <p className="text-[#5A8BA8] text-sm mb-4 line-clamp-3">
                        {featuredArticle.excerpt}
                      </p>
                      <span className="inline-flex items-center text-[#0C63BF] font-semibold text-sm gap-1 group-hover:gap-2 transition-all">
                        {t('blog.read_article')} <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            )}

            {/* Articles grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(selectedCategory ? articles : otherArticles).map(article => (
                <Link key={article.id} to={`/blog/${article.slug}`} data-testid={`article-${article.slug}`}>
                  <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all group h-full flex flex-col">
                    <div className="h-44 relative overflow-hidden">
                      <img 
                        src={getArticleImage(article)} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                      <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold ${CATEGORY_COLORS[article.category]}`}>
                        {t(`blog.cat_${article.category}`)}
                      </span>
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-[#B8D4E3] text-xs mb-2">
                        <Clock className="w-3 h-3" /> {t('blog.min_read', { n: article.read_time })}
                      </div>
                      <h3 className="text-base font-bold text-[#033871] mb-2 group-hover:text-[#0C63BF] transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-[#5A8BA8] text-sm line-clamp-2 flex-1">
                        {article.excerpt}
                      </p>
                      <span className="inline-flex items-center text-[#0C63BF] font-semibold text-sm gap-1 mt-4 group-hover:gap-2 transition-all">
                        {t('blog.read')} <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-br from-[#033871] to-[#0C63BF] rounded-2xl p-8 text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
            {t('blog.cta_title')}
          </h2>
          <p className="text-white/70 mb-5 max-w-lg mx-auto">
            {t('blog.cta_sub')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/catalogue">
              <Button className="bg-[#32CD32] hover:bg-[#28a428] text-white font-bold gap-2" data-testid="cta-catalogue">
                <Search className="w-4 h-4" /> {t('blog.cta_listings')}
              </Button>
            </Link>
            <Link to="/estimer">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold gap-2" data-testid="cta-estimate">
                <TrendingUp className="w-4 h-4" /> {t('blog.cta_estimate')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
